// Valeja ostajapolku: vaiheet, ilmakanavan video, kauhavalitsin, koriin lisäys, ostopalkki, lukunavigaatio ja analytiikkatapahtumat.
// Ladataan type="module" jokaisesta valeja-*-osiosta; init on idempotentti (data-vj-ready).
import { CartAddEvent } from '@theme/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Ostajapolun omat analytiikkatapahtumat. Shopifyn mukautettu pikseli (Asiakastapahtumat) välittää ne GA4:ään
 * evästesuostumuksen mukaan. Nimet ja parametrit: 04_MARKKINOINTI/OSTAJAPOLKU-GA4-TAPAHTUMAT.md.
 */
function track(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (err) {
    console.error(err);
  }
}

/** Tuotteen handle -> koko-parametrin arvo. */
const KOKO = {
  sadekauha: 'sadekauha',
  'valeja-sadekauha-mestari': 'mestari',
  'valeja-sadekauha-iso': 'pitka-mestari',
  'valeja-sadekansi-anela': 'sadekansi',
};
const kokoFromUrl = (href) => KOKO[(String(href || '').match(/\/products\/([^/?#]+)/) || [])[1]] || 'muu';
/** Valitsimen suositus -> koko. */
const REC_KOKO = { soft: 'sadekauha', pair: 'sadekauha+mestari', far: 'pitka-mestari' };

/** Vaihepainikkeet, jotka vaihtavat [data-vj-state] -kohteen tilaa. Palauttaa { set, onPick }. */
function initSteps(root) {
  const target = root.querySelector('[data-vj-state]');
  const buttons = [...root.querySelectorAll('[data-vj-step]')];
  if (!target || !buttons.length) return null;
  const pickHandlers = [];
  const shots = [...root.querySelectorAll('[data-vj-shot]')];
  let current = 1;
  let timer = null;
  const set = (s) => {
    current = s;
    target.setAttribute('data-s', String(s));
    buttons.forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.vjStep) === s)));
    shots.forEach((el) => el.classList.toggle('is-on', Number(el.dataset.vjShot) === s));
  };
  buttons.forEach((b) =>
    b.addEventListener('click', () => {
      if (timer) clearInterval(timer);
      timer = null;
      set(Number(b.dataset.vjStep));
      pickHandlers.forEach((fn) => fn(current));
    })
  );
  set(1);
  if (!reduceMotion && root.hasAttribute('data-vj-autoplay')) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !timer) timer = setInterval(() => set((current % 3) + 1), 3200);
        if (!e.isIntersecting && timer) {
          clearInterval(timer);
          timer = null;
        }
      }
    });
    io.observe(root);
  }
  return { set, onPick: (fn) => pickHandlers.push(fn) };
}

/**
 * Ilmakanavan video ohjaa vaiheita: data-marks = vaiheiden alkuajat (s). Napin painallus hyppää
 * vaiheen alkuun. Lähde valitaan leveyden mukaan ja ladataan vasta, kun video tulee näkyviin;
 * ruudun ulkopuolella video pysähtyy. Reduced motion: ei automaattitoistoa, napit näyttävät ruudun.
 */
function initVideo(root, steps) {
  const video = root.querySelector('[data-vj-video]');
  if (!video || !steps) return;
  const marks = String(video.dataset.marks || '').split(',').map(Number).filter((n) => !Number.isNaN(n));
  const phaseAt = (t) => marks.reduce((p, m, i) => (t >= m ? i + 1 : p), 1);
  const mobile = window.matchMedia('(max-width: 699px)').matches;
  const src = (mobile && video.dataset.srcM) || video.dataset.srcD;
  let loaded = false;
  let lastPhase = 0;
  const load = () => {
    if (loaded || !src) return;
    loaded = true;
    video.src = src;
    video.preload = 'auto';
  };
  video.addEventListener('timeupdate', () => {
    const p = phaseAt(video.currentTime);
    if (p !== lastPhase) {
      lastPhase = p;
      steps.set(p);
    }
  });
  steps.onPick((s) => {
    load();
    lastPhase = s;
    video.currentTime = (marks[s - 1] || 0) + 0.05;
    if (!reduceMotion) video.play().catch(() => {});
  });
  let inView = false;
  const resume = () => {
    if (inView && !reduceMotion && document.visibilityState === 'visible') video.play().catch(() => {});
  };
  new IntersectionObserver(
    (es) => {
      for (const e of es) {
        inView = e.isIntersecting;
        if (inView) {
          load();
          resume();
        } else if (!video.paused) video.pause();
      }
    },
    { rootMargin: '200px 0px', threshold: 0.25 }
  ).observe(video);
  // Selain keskeyttää piilossa olevan välilehden videon; jatketaan, kun sivu palaa näkyviin.
  document.addEventListener('visibilitychange', resume);
}

/**
 * Toimitusajalla myytävien kauhojen lähtöpäivä tilausrivin ominaisuutena (snippets/valeja-assets.liquid,
 * [data-vj-era-props]): variantti-id -> { "Lähtee": "viimeistään to 12.11." }. Näkyy korissa, kassalla ja tilauksessa.
 */
function eraProps() {
  const out = {};
  document.querySelectorAll('script[data-vj-era-props]').forEach((el) => {
    try {
      Object.assign(out, JSON.parse(el.textContent || '{}'));
    } catch (err) {
      console.error(err);
    }
  });
  return out;
}

/** Lisää variantit koriin ja ilmoittaa teeman koriin (kori-ikoni + sivulaatikko). */
async function addToCart(items, button) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const props = eraProps();
  items = items.map((i) => (props[i.id] ? { ...i, properties: props[i.id] } : i));
  const original = button ? button.innerHTML : '';
  if (button) button.disabled = true;
  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'cart error');
    const count = items.reduce((n, i) => n + (i.quantity || 1), 0);
    document.dispatchEvent(
      new CartAddEvent({}, 'valeja-ostajapolku', {
        source: 'product-form-component',
        itemCount: count,
        variantId: String(items[0].id),
      })
    );
    if (button) {
      button.innerHTML = button.dataset.addedLabel || original;
      setTimeout(() => (button.innerHTML = original), 1800);
    }
    // Teemassa kori on sivulaatikko; jos se ei ole käytössä, ohjataan koriin.
    if (!document.querySelector('cart-drawer-component')) window.location.href = `${root}cart`;
  } catch (err) {
    console.error(err);
    if (button) {
      button.innerHTML = button.dataset.errorLabel || original;
      setTimeout(() => (button.innerHTML = original), 2500);
    }
  } finally {
    if (button) button.disabled = false;
  }
}

function initAddButtons(root) {
  root.querySelectorAll('[data-vj-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      // Tuotesivun palkki: käytetään teeman omaa ostolomaketta, jos se on sivulla.
      if (btn.closest('[data-vj-productbar]')) {
        const main = document.querySelector('product-form-component .add-to-cart-button:not([disabled])');
        if (main) {
          main.click();
          return;
        }
      }
      const ids = String(btn.dataset.vjAdd).split(',').filter(Boolean);
      addToCart(
        ids.map((id) => ({ id: Number(id), quantity: 1 })),
        btn
      );
    });
  });
}

/** Kauhavalitsin: kaksi kysymystä -> suositus. */
function initPicker(root) {
  const picker = root.querySelector('[data-vj-picker]');
  if (!picker) return;
  const recs = {};
  picker.querySelectorAll('[data-vj-rec]').forEach((el) => (recs[el.dataset.vjRec] = el));
  const answers = { size: 'small', style: 'soft' };
  const pick = () => {
    if (answers.style === 'pro') return 'pro';
    if (answers.style === 'far') return 'far';
    if (answers.style === 'hot' || answers.size === 'big') return 'pair';
    return 'soft';
  };
  const render = () => {
    const key = pick();
    Object.entries(recs).forEach(([k, el]) => (el.hidden = k !== key));
  };
  picker.querySelectorAll('[data-vj-q]').forEach((group) => {
    group.addEventListener('click', (e) => {
      const chip = e.target instanceof Element ? e.target.closest('[data-v]') : null;
      if (!chip) return;
      group.querySelectorAll('[data-v]').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
      answers[group.dataset.vjQ] = chip.dataset.v;
      render();
      track('valeja_koko_valittu', { koko: REC_KOKO[pick()] });
    });
  });
  render();
}

/** Ostopalkki: näkyy kun ensimmäinen osio on ohitettu, piiloon valitse-osiossa ja alatunnisteessa (somelinkit jäivät alle, 10.10.2026). */
function initBuybar() {
  const bar = document.querySelector('[data-vj-buybar]');
  if (!bar || bar.dataset.vjReady) return;
  bar.dataset.vjReady = '1';
  const first = document.querySelector('#MainContent > .shopify-section');
  const pick = document.getElementById('valitse');
  const foot = [...document.querySelectorAll('.shopify-section-group-footer-group')].pop();
  let pastFirst = false;
  let inPick = false;
  let inFoot = false;
  const upd = () => bar.classList.toggle('is-on', pastFirst && !inPick && !inFoot);
  if (foot)
    new IntersectionObserver((es) => {
      inFoot = es[0].isIntersecting;
      upd();
    }).observe(foot);
  if (first)
    new IntersectionObserver((es) => {
      pastFirst = !es[0].isIntersecting;
      upd();
    }, { threshold: 0.1 }).observe(first);
  if (pick)
    new IntersectionObserver((es) => {
      inPick = es[0].isIntersecting;
      upd();
    }, { threshold: 0.15 }).observe(pick);
}

/**
 * Tuotesivun palkki: näkyy, kun teeman ostonappi on vieritetty ylös pois näkyvistä (sivun alussa ei näy).
 * Teema voi korvata ostolomakkeen napin uudella elementillä (morph), jolloin yhden elementin tarkkailu jää
 * irralliseen nappiin. MutationObserver vaihtaa tarkkailun uuteen nappiin.
 */
function initProductBar() {
  const bar = document.querySelector('[data-vj-productbar]');
  // Oma merkintä: palkilla on myös data-vj (koriin lisäys), jonka init() merkitsee vjReady:ksi ennen tätä,
  // joten yhteinen vjReady-tarkistus lopetti alustuksen eikä palkki koskaan noussut (korjattu 9.10.2026).
  if (!bar || bar.dataset.vjBarReady) return;
  bar.dataset.vjBarReady = '1';
  // Tarkkailualue jatkuu näkymän alapuolelle, joten ainoa raja on näkymän yläreuna: muutos huomataan myös,
  // kun nappi hyppää kerralla alapuolelta yläpuolelle (mobiilissa nappi on aluksi näkymän alla, nopea pyyhkäisy).
  const io = new IntersectionObserver(
    (es) => {
      const e = es[es.length - 1];
      bar.classList.toggle('is-on', !e.isIntersecting && e.boundingClientRect.bottom < 0);
    },
    { rootMargin: '0px 0px 100000px 0px' }
  );
  let watched = null;
  const watch = () => {
    const main = document.querySelector('product-form-component .add-to-cart-button');
    if (main === watched) return;
    if (watched) io.unobserve(watched);
    watched = main;
    if (main) io.observe(main);
    else bar.classList.remove('is-on');
  };
  watch();
  new MutationObserver(watch).observe(document.body, { childList: true, subtree: true });
}

/** Pääpainikkeen nimi nappi-parametriin, tai null jos klikkausta ei seurata. */
function ctaName(el) {
  if (el.closest('[data-vj-buybar]')) return 'ostopalkki';
  if (el.closest('[data-vj-productbar]')) return 'tuotepalkki';
  if (el.closest('[data-vj-picker]')) return el.matches('[data-vj-add]') ? 'valitsin-koriin' : null;
  if (el.closest('.vj-size')) return el.matches('[data-vj-add]') ? 'kortti-koriin' : 'kortti-lisatietoa';
  const sec = el.closest('.shopify-section');
  if (sec && /__hero/.test(sec.id) && el.matches('a')) return `hero-${[...sec.querySelectorAll('a')].indexOf(el) + 1}`;
  return null;
}

/**
 * Sivunlaajuiset seurannat, kerran per sivu: pääpainikkeet (valeja_cta, kortista myös valeja_koko_valittu)
 * ja videot (valeja_sadevideo: alku ja 75 %). Taustalla pyöriviä (hero, data-vj-notrack) ei seurata.
 */
let tracking = false;
function initTracking() {
  if (tracking) return;
  tracking = true;
  document.addEventListener('click', (e) => {
    const el = e.target instanceof Element ? e.target.closest('a, button') : null;
    if (!el) return;
    const nappi = ctaName(el);
    if (!nappi) return;
    track('valeja_cta', { nappi });
    const card = el.closest('.vj-size');
    if (card) track('valeja_koko_valittu', { koko: kokoFromUrl(card.querySelector('a[href]')?.getAttribute('href')) });
  });
  const seen = new WeakMap();
  const videoName = (v) => (v.matches('[data-vj-video]') ? 'ilmakanava' : 'tuotevideo');
  const isTracked = (t) => t instanceof HTMLVideoElement && !t.matches('[data-vj-hero-video], [data-vj-notrack]');
  document.addEventListener(
    'play',
    (e) => {
      const v = e.target;
      if (!isTracked(v) || seen.has(v)) return;
      seen.set(v, false);
      track('valeja_sadevideo', { video: `${videoName(v)}:start` });
    },
    true
  );
  document.addEventListener(
    'timeupdate',
    (e) => {
      const v = e.target;
      if (!isTracked(v) || seen.get(v) !== false || !(v.duration > 0) || v.currentTime / v.duration < 0.75) return;
      seen.set(v, true);
      track('valeja_sadevideo', { video: `${videoName(v)}:75` });
    },
    true
  );
}

/** Lukunavigaatio korostaa näkyvän luvun. */
function initNav() {
  const nav = document.querySelector('[data-vj-nav]');
  if (!nav || nav.dataset.vjReady) return;
  nav.dataset.vjReady = '1';
  const list = nav.querySelector('ol');
  const links = new Map();
  nav.querySelectorAll('a[href^="#"]').forEach((a) => links.set(a.getAttribute('href').slice(1), a));
  const io = new IntersectionObserver(
    (es) => {
      for (const e of es) {
        if (!e.isIntersecting) continue;
        links.forEach((a, id) => a.classList.toggle('is-on', id === e.target.id));
        // Kapealla ruudulla rivi vierii niin, että aktiivinen luku on keskellä.
        const on = links.get(e.target.id);
        if (on && list.scrollWidth > list.clientWidth) {
          list.scrollTo({ left: on.offsetLeft - (list.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
        }
      }
    },
    { rootMargin: '-45% 0px -50% 0px' }
  );
  links.forEach((_, id) => {
    const el = document.getElementById(id);
    if (el) io.observe(el);
  });
}

function init() {
  document.querySelectorAll('[data-vj]').forEach((root) => {
    if (root.dataset.vjReady) return;
    root.dataset.vjReady = '1';
    initVideo(root, initSteps(root));
    initAddButtons(root);
    initPicker(root);
  });
  initBuybar();
  initProductBar();
  initTracking();
  initNav();
}

init();
document.addEventListener('shopify:section:load', init);
